import type { DialogStore } from '@ariakit/react';
import { Dialog } from '@ariakit/react';
import Button, { ButtonVariant } from '@ben/orion/deprecated-button';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { Dispatch, FC, SetStateAction } from 'react';
import { memo, useCallback } from 'react';
import styled, { css } from 'styled-components';

interface IRenameTableConfirmationModal {
  dialog: DialogStore;
  filename: string;
  incomingTableName: string;
  handleSaveRename: () => void;
  setTableName: (tableName: string) => void;
  setReRenderKey: Dispatch<SetStateAction<number>>;
}

const RenameTableConfirmationModal: FC<IRenameTableConfirmationModal> = ({
  dialog,
  filename,
  incomingTableName,
  handleSaveRename,
  setTableName,
  setReRenderKey,
}) => {
  const handleSave = useCallback(() => {
    handleSaveRename();
    dialog.hide();
  }, [dialog, handleSaveRename]);

  const handleCancel = useCallback(() => {
    setTableName(incomingTableName);
    setReRenderKey((previous) => previous + 1);
    dialog.hide();
  }, [dialog, incomingTableName, setReRenderKey, setTableName]);

  return (
    <DialogContainer store={dialog} aria-modal="true">
      <DialogContent>
        <ContentContainer>
          <Typography variant={TypographyVariant.h2}>
            Rename &apos;{filename}&apos;
          </Typography>
          <Typography variant={TypographyVariant.bodyLg1}>
            This rate table name is currently referenced to a pricing formula
            for this benefit. Modifying the name will cause the pricing formula
            to error. Are you sure you want to continue?
          </Typography>
        </ContentContainer>
        <ActionsContainer>
          <Button
            label="Cancel"
            variant={ButtonVariant.secondary}
            onClick={handleCancel}
            width="208px"
          />
          <Button label="Continue" onClick={handleSave} width="208px" />
        </ActionsContainer>
      </DialogContent>
    </DialogContainer>
  );
};

export default memo(RenameTableConfirmationModal);

const DialogContainer = styled(Dialog)`
  ${({ theme }) => css`
    position: fixed;
    inset: 0.75rem;
    z-index: 50;
    margin: auto;
    background-color: ${theme.palette.black(0.7)};
    display: flex;
    justify-content: center;
    align-items: center;
    margin-left: ${theme.spacing(-1.5)};
    width: 100vw;
    height: 100vh;
  `}
`;

const DialogContent = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.palette.neutral['00']};
    border-radius: ${theme.borderRadius.sm};
    padding: ${theme.spacing(5)};
    width: 520px;
    height: fit-content;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    align-items: center;
    justify-content: center;
  `}
`;

const ContentContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    margin-top: ${theme.spacing(3)};
  `}
`;

const ActionsContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-top: ${theme.spacing(5)};
    width: 100%;
  `}
`;
